/* eslint-disable max-statements */
/* eslint-disable complexity */


import React, { useContext, useRef } from 'react';

import { BlueprintInstanceType, Components, isTopModelSku, PanelItem, type Size, Vertex2 } from '@rta/rta-blueprint-library';

import { addContextMenu } from '../../context-menu';
import { AppContext } from '@r-app/app-context';
import { APPLIANCE_FONT_SIZE, DIMENSION_FONT_SIZE } from '@r-core/constants';
import { DrawPanelItemDimensions } from '../draw-dimensions/draw-panel-item-dimensions';
import { getPanelItemVectors } from '@r-core/helpers';
import { Layer } from '@r-core/types';
import { renderTracking } from '@r-services/render-tracking';
import { svgPolygonFromVectors } from '../helpers/svg-polygon-from-vectors';
import { usePanelItem } from '@r-hooks';

import { DesignerOptions, DisplayMode } from '@r-core';
import { useDraggableSvg, type UseDraggableSvgProps } from '@r-services/ui-interactions';

export type RenderCountertopPanelItemProps = {
	panelItem: PanelItem;
	designerOptions: DesignerOptions;
};

export const RenderCountertopPanelItem = ({ panelItem }: RenderCountertopPanelItemProps): React.ReactElement => {
	const {
		controller: { options, navigation }
	} = useContext(AppContext);

	const movableRef = useRef(null);
	const uPanelItem = usePanelItem(panelItem, { isCountertop: true, options });
	const { panel, island, panelItemGroup } = panelItem.refMap;

	const itemVectors = getPanelItemVectors({ panelItem, isCountertop: true });
	// console.log('RenderCountertopPanelItem:itemVectors', itemVectors);

	const overlayVectors = itemVectors.filter((vector) => vector.layer === Layer.Overlay);
	const cutoutVectors = itemVectors.filter((vector) => vector.layer === Layer.Panel);

	const isAlwaysVisibleOnCountertop = ['POP-UP-OUTLET', 'UMBRELLA-CUTOUT'].includes(panelItem.item.label);

	const renderImageInSkuMode = panelItem.item.showInSkuOverhead && panelItem.item.skuOverheadMode === 'image';
	let applianceOpacity = 1;
	let cutoutOpacity = 1;
	let { showAppliances, showCutouts } = options;
	switch (navigation.displayMode) {
		case DisplayMode.Manufacturing:
		case DisplayMode.InlineManufacturing:
		case DisplayMode.OverheadManufacturing:
			cutoutOpacity = 1;
			showAppliances = false;
			// Manufacturing views are the source-of-truth for fabrication —
			// always show cutout outlines + svg.item annotations regardless of
			// the user's designer-options toggle. Mirrors draw-panel-item-dimensions.
			showCutouts = true;
			break;
		case DisplayMode.SkuOverheadManufacturing:
			showAppliances = false;
			showCutouts = renderImageInSkuMode;
			break;
		case DisplayMode.SkuOverhead:
			showAppliances = false;
			showCutouts = renderImageInSkuMode;
			break;
		default:
			// max 75%, we dont have images so let some of the cutout show through
			applianceOpacity = 0.75;
			cutoutOpacity = 1;
	}
	const customLabel = panelItem.item.customLabel ? panelItem.item.customLabel : null;
	const label = customLabel ? customLabel : panelItem.item.displayText;
	const applianceFontSize = APPLIANCE_FONT_SIZE * options.appliance.fontSizeFactor;
	const yLabel = uPanelItem._label.origin.z;
	const xLabel: number = uPanelItem._label.origin.x;
	const alignLabel = 'end';
	const countertop = island.getCountertop();

	// Top-only items that are alone in their group (umbrella cutouts, gas line
	// cutouts, kegerator taps) can also be dragged horizontally -- the drag moves
	// their group's x, which PanelItemGroup.move clamps to the countertop width.
	// Items sharing a group with other appliances keep depth-only dragging so a
	// drag never relocates their siblings.
	const isTopOnly = panelItem.item.placement === Components.PLACEMENT_TOP_ONLY;
	const isSoloGroup = (panelItemGroup?.items.children.length ?? 0) === 1;
	const canDragX = isTopOnly && isSoloGroup;

	const draggable: UseDraggableSvgProps = {
		ref: movableRef,
		enabled: [canDragX, isTopOnly],
		cursor: ['inherit', 'move'],
		position: { x: (panelItemGroup?.center.x ?? 0) + panelItem.center.x, y: panelItem.center.z },
		// The box has to sit on the COUNTERTOP, which is only centred on the island when its overhangs are
		// symmetric: `countertop.center` carries (right-left)/2 and (front-back)/2. A seated-bar island's
		// 10" back overhang puts that centre 4" behind the island's, and anchoring the box at 0 / the front
		// panel's plane instead left the cutout 4" short of the back edge -- bug #653. Padding keeps the
		// whole cutout on the slab; x had none, so it could hang half off the side.
		bBox: {
			origin: new Vertex2({ dataIn: { x: countertop?.center.x ?? 0, y: (countertop?.center.z ?? 0) - (panel?.center.z ?? 0) }}),
			size: {
				width: countertop?.size.width ?? 0,
				height: countertop?.size.height ?? 0,
				depth: 0
			},
			padding: [panelItem.size.width / 2, panelItem.size.depth / 2, panelItem.size.width / 2, panelItem.size.depth / 2]
		},
		onDragging: ({ position: { new: newPosition }}) => {
			if (canDragX) panelItemGroup.move({ x: newPosition.x - panelItem.center.x }, true);
			panelItem.move({ x: 0, z: newPosition.y });
			return true;
		}
	};
	// console.log('RenderCountertopPanelItem:draggable', draggable);
	useDraggableSvg(draggable);

	const contextMenu = { menuKey: BlueprintInstanceType.PanelItem, id: panelItem.id };
	const isSpacer = panelItem.item.category?.isSpacer ?? false;
	const isManufacturingView = navigation.displayMode === DisplayMode.Manufacturing ||
		navigation.displayMode === DisplayMode.OverheadManufacturing ||
		navigation.displayMode === DisplayMode.SkuOverheadManufacturing;

	// Spacers do not render in manufacturing views
	if (isSpacer && isManufacturingView) return <></>;

	// Spacer items render a dashed outline + label instead of filled cutout
	if (isSpacer) {
		const spacerLabel = panelItem.item.customLabel || panelItem.item.displayText;
		return (
			<>
				<g
					ref={movableRef}
					id={panelItem.id}
					overflow="visible"
					transform={`translate(${panelItem.center.x}, ${panelItem.center.z})`}
					type="panel-item-spacer"
					{...addContextMenu(contextMenu)}
				>
					<rect
						x={-panelItem.size.width / 2}
						y={-panelItem.size.depth / 2}
						width={panelItem.size.width}
						height={panelItem.size.depth}
						fill="lightgray"
						fillOpacity={0.4}
						stroke="darkblue"
						strokeWidth="0.15"
						strokeDasharray=".5"
						pointerEvents="all"
					/>
					<text
						textAnchor="middle"
						x={0}
						y={applianceFontSize * 0.35}
						fontSize={applianceFontSize}
						className="rta-designer-component-label rta-noselect"
					>
						{spacerLabel}
					</text>
					<DrawPanelItemDimensions panelItemKey={panelItem.id} isCountertop={true} />
				</g>
			</>
		);
	}

	// Instance rotation for overhead view: Z rotation spins on the countertop
	const { z: irZ } = panelItem.effectiveRotation;
	const overheadRotationDeg = -irZ * (180 / Math.PI);

	renderTracking.incrementRender('RenderCountertopPanelItem');
	return <>
		<g ref={movableRef}
			id={panelItem.id}
			overflow="visible"
			transform={`translate(${panelItem.center.x}, ${panelItem.center.z})${overheadRotationDeg !== 0 ? ` rotate(${overheadRotationDeg})` : ''}`}
			type="panel-item-appliance"
			{ ...addContextMenu(contextMenu)}
			// {...events}
		>
			{panelItem.item.placement === Components.PLACEMENT_FRONT_AND_TOP
				? <>
					{/**  Cutout **/}
					{showCutouts && panelItem.sleeve === null && panelItem.isStandAlone
						? svgPolygonFromVectors(
								itemVectors.filter((vector) => vector.layer === Layer.Overlay),
								{ fill: 'white', opacity: cutoutOpacity }
						)
						: panelItem.sleeve === null && svgPolygonFromVectors(
								itemVectors.filter((vector) => vector.layer === Layer.Panel),
								{ fill: 'white', opacity: cutoutOpacity }
						)}
					{/** Appliance Overlay **/}
					{showAppliances && panelItem.sleeve === null && overlayVectors.length > 0 && svgPolygonFromVectors(overlayVectors, { fill: 'grey', opacity: applianceOpacity })}
				</>
				: <>
					{/**  Cutout - use SVG cutout if available, otherwise polygon **/}
					{(showCutouts || isAlwaysVisibleOnCountertop) && (
						panelItem.item.component.svg?.cutout({ fill: options.backgroundColor, opacity: cutoutOpacity, diameter: panelItem.item.cutout.width })
						?? (cutoutVectors.length > 0 && svgPolygonFromVectors(cutoutVectors, { fill: options.backgroundColor, stroke: 'none', strokeWidth: '0', opacity: cutoutOpacity }))
					)}
					{/* Top-model SKUs render wording via RenderCountertopTopModelLabels; offPanelDetail SKUs (#282/#385) via RenderCountertopCutoutDetailBlocks -- never here. */}
					{(showCutouts || isAlwaysVisibleOnCountertop) && !isTopModelSku(panelItem.item.sku) && !panelItem.item.component.svg?.offPanelDetail && panelItem.item.component.svg?.item?.({ diameter: panelItem.item.cutout.width, fontSize: DIMENSION_FONT_SIZE * options.dimensions.fontSizeFactor })}
					{/** Appliance Overlay - skip when an SVG handles the visual (e.g. the kegerator tap), matching overhead **/}
					{showAppliances && !panelItem.item.component.svg && overlayVectors.length > 0 && svgPolygonFromVectors(overlayVectors, { fill: 'grey', stroke: 'none', strokeWidth: '.2', opacity: applianceOpacity })}
				</>
			}

			{/* Draw Component Label */}
			{uPanelItem._label.show && panelItem.sleeve === null && (
				<text textAnchor={alignLabel} x={xLabel} y={yLabel} fontSize={applianceFontSize} className="rta-designer-component-label">
					{label}
				</text>
			)}
			{/* Selection box for draggable top-only items - rendered last to be on top */}
			{panelItem.item.placement === Components.PLACEMENT_TOP_ONLY && <rect
				x={-panelItem.size.width / 2}
				y={-panelItem.size.depth / 2}
				width={panelItem.size.width}
				height={panelItem.size.depth}
				fill="white"
				stroke="none"
				opacity={0}
				pointerEvents="all"
			/>}
		</g>

		{/* PanelItem dimensions — wrapped with rotation so they follow the rotated appliance */}
		<g transform={overheadRotationDeg !== 0 ? `translate(${panelItem.center.x}, ${panelItem.center.z}) rotate(${overheadRotationDeg}) translate(${-panelItem.center.x}, ${-panelItem.center.z})` : undefined}>
			<DrawPanelItemDimensions panelItemKey={panelItem.id} isCountertop={true} />
		</g>

		{options.showDeveloperInsights && (
			<>
			</>
		)}
	</>
};
